import React from 'react';
import PropTypes from 'prop-types';
import { ProTile } from '@thd-olt-component-react/project-summary';
import { Carousel } from '@one-thd/sui-carousel';
import classNames from 'classnames';

import { projectPropType } from '../dataModel';
import { desktopCarouselContainerClass } from '../../constants/responsiveClassNames';

const { arrayOf, bool } = PropTypes;

const DesktopCarousel = ({ projects, isDashboardSnapshot, fillHeight }) => {
  // fillHeight: the host sizes the card, so the floor below would only stop it shrinking.
  const hasHeightFloor = isDashboardSnapshot && !fillHeight;

  return (
    <div
      id="DesktopCarousel"
      data-testid="DesktopCarousel"
      className={classNames(desktopCarouselContainerClass, {
        // Under fillHeight the cards fill this container, so .swiper is pinned to its full
        // height after all - with a bottom band (!sui-pb-2) that the wrapped scrollbar line
        // lands in, instead of past the clipped edge (the WOR2-973 failure described on
        // desktopCarouselContainerClass). Same mechanism as the Quotes carousel. The ! beats
        // the SUI reset's #sui-carousel-resets .swiper padding.
        '[&_.swiper]:sui-h-full [&_.swiper]:!sui-pb-2': fillHeight,
      })}
    >
      <Carousel
        type="peek"
        layout="full-bleed"
        containerId="DesktopCarousel"
        slidesPerView="auto"
        slidesPerGroup={1}
        spaceBetween={0}
        breakpoints={{
          sm: { slidesPerGroup: 1, slidesPerView: 'auto' },
          md: { slidesPerGroup: 1, slidesPerView: 'auto' },
          lg: { slidesPerGroup: 1, slidesPerView: 'auto' },
          xl: { slidesPerGroup: 1, slidesPerView: 'auto' },
          '2xl': { slidesPerGroup: 1, slidesPerView: 'auto' },
        }}
        disableMargin
      >
        {projects && projects.map((project, index) => (
          <div
            key={project.projectId}
            className={classNames('sui-w-72 @lg:sui-w-80 @3xl:sui-w-96 [&>div]:sui-h-full', {
              'sui-mr-4': index !== projects.length - 1,
              'sui-grid': isDashboardSnapshot,
            })}
            // 29rem is a real card's own natural height (measured against a populated card
            // elsewhere with a comparable field set), not an arbitrary number — it keeps
            // this tab's cards from looking noticeably shorter than sibling cards in other
            // Dashboard Snapshot tabs. Desktop carousel only: the mobile compact list has no
            // sibling row to match, so a floor there is just dead space. A grid minmax()
            // floor, not a fixed height: shorter cards lift to it, taller ones still grow
            // past it (verified live — no clipping). min-height alone on this div wouldn't
            // reach ProTile's own Card below: a percentage height only resolves against a
            // parent with a definite size, and min-height by itself doesn't make one. No
            // Tailwind utility covers minmax(), so this has to be inline. Skipped under
            // fillHeight: the host then sets the height, and the card fills it.
            style={hasHeightFloor ? { gridTemplateRows: 'minmax(29rem, auto)' } : undefined}
          >
            <ProTile
              projectId={project.projectId}
              project={project}
              maxThumbnails={4}
              impressionName="ProjectsOverview"
              position={index + 1}
              isDashboardSnapshot={isDashboardSnapshot}
            />
          </div>
        ))}
      </Carousel>
    </div>
  );
};

DesktopCarousel.propTypes = {
  projects: arrayOf(projectPropType),
  isDashboardSnapshot: bool,
  fillHeight: bool,
};

DesktopCarousel.defaultProps = {
  projects: [],
  isDashboardSnapshot: false,
  fillHeight: false,
};

export default DesktopCarousel;
